import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AI_FEEDBACK, AI_FEEDBACK_LABELS, type AiFeedback } from '@raro/domain';
import { Button, Label, Notice, Segments, Tag } from '@raro/ui';
import { Link } from 'react-router';
import { useAiStatus } from '../lib/ai';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import type { Challenge, Suggestion } from '../lib/types';
import { useSuggestionActions } from './Suggestions';

/** REMAR CONTRA — o agente adversarial (blueprint §9). */
export function ChallengePanel({ hypothesisId, projectId, canEdit }: { hypothesisId: string; projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const status = useAiStatus();
  const list = useQuery({ queryKey: ['challenges', hypothesisId], queryFn: () => api.get<Challenge[]>(`/hypotheses/${hypothesisId}/challenges`) });
  const suggestions = useQuery({ queryKey: ['suggestions', projectId, 'pending'], queryFn: () => api.get<Suggestion[]>(`/projects/${projectId}/suggestions?status=pending`) });
  const { accept } = useSuggestionActions(projectId);
  const run = useMutation({
    mutationFn: () => api.post<Challenge>(`/hypotheses/${hypothesisId}/challenge`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['challenges', hypothesisId] });
      void qc.invalidateQueries({ queryKey: ['suggestions', projectId] });
      void qc.invalidateQueries({ queryKey: ['ai-costs', projectId] });
    },
  });
  const feedback = useMutation({
    mutationFn: ({ id, f }: { id: string; f: AiFeedback }) => api.post(`/challenges/${id}/feedback`, { feedback: f }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['challenges', hypothesisId] }),
  });
  const llm = Boolean(status.data?.llm);
  const latest = list.data?.[0] ?? null;
  const questionSuggestions = (suggestions.data ?? []).filter((s) => s.feature === 'challenge' && s.payload.challengeId === latest?.id);

  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <div className="rr-challenge__head">
          <h2 className="rr-challenge__title"><span className="rr-challenge__arrow">↗</span> Remar contra</h2>
          {latest && <Tag tone="accent">IA em modo crítico</Tag>}
        </div>
        {canEdit && (
          <Button variant={latest ? 'ghost' : 'primary'} size="sm" onClick={() => run.mutate()} disabled={!llm || run.isPending}>
            {run.isPending ? 'remando…' : latest ? 'remar de novo →' : 'remar contra →'}
          </Button>
        )}
      </div>
      {status.data && !llm && <Notice tone="error">sem modelo configurado. defina ANTHROPIC_API_KEY (ou AI_LLM_PROVIDER=mock).</Notice>}
      {run.isError && <Notice tone="error">{(run.error as Error).message}</Notice>}
      {!latest && !run.isPending && llm && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>quem já faz isso? por que pode estar errado? qual evidência contradiz? estamos repetindo a linguagem do mercado? objetivo: reduzir o enamoramento por ideias.</p>}
      {latest && (
        <div className="rr-challenge">
          <div>
            {latest.content.whyItMightBeWrong.length > 0 && (
              <div className="rr-challenge__block">
                <p className="rr-challenge__lead">Isso pode ser comum demais.</p>
                <ul>{latest.content.whyItMightBeWrong.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}
            {latest.content.marketLanguage.repeatsMarket && (
              <div className="rr-challenge__block">
                <Label>Linguagem do mercado</Label>
                <p>{latest.content.marketLanguage.note}</p>
                {latest.content.marketLanguage.phrases.length > 0 && <p className="rr-faint">{latest.content.marketLanguage.phrases.map((p) => `“${p}”`).join(', ')}</p>}
              </div>
            )}
            {latest.content.whoAlreadyDoesIt.length > 0 && (
              <div className="rr-challenge__block">
                <Label>Quem já faz isso</Label>
                <ul>{latest.content.whoAlreadyDoesIt.map((w, i) => <li key={i}><strong style={{ fontWeight: 500, color: 'var(--rr-text)' }}>{w.who}</strong> — {w.how}</li>)}</ul>
              </div>
            )}
            {latest.content.contradictingEvidence.length > 0 && (
              <div className="rr-challenge__block">
                <Label>O que contradiz</Label>
                <ul>{latest.content.contradictingEvidence.map((c, i) => <li key={i}>{c.sourceId ? <Link to={`/fontes/${c.sourceId}`} style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>“{c.text}”</Link> : `“${c.text}”`} — {c.why}</li>)}</ul>
              </div>
            )}
            {latest.content.unprovenPremises.length > 0 && (
              <div className="rr-challenge__block">
                <Label>Premissas não provadas</Label>
                <ul>{latest.content.unprovenPremises.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </div>
            )}
            <div className="rr-challenge__block">
              <Label>Como um concorrente atacaria</Label>
              <p className="rr-muted">{latest.content.competitorAttack}</p>
            </div>
            <div className="rr-challenge__block">
              <Label>E se o oposto fosse verdadeiro?</Label>
              <p className="rr-muted">{latest.content.oppositeCouldBeTrue.plausible ? 'plausível. ' : 'improvável. '}{latest.content.oppositeCouldBeTrue.argument}</p>
            </div>
          </div>
          <aside className="rr-stack rr-stack--5">
            <div className="rr-card rr-stack rr-stack--3">
              <Label>Risco de comoditização</Label>
              <div className="rr-risk__row">
                <Segments value={latest.commoditizationRisk ?? 0} total={10} />
                <span className="rr-risk__value">{latest.commoditizationRisk ?? '—'}%</span>
              </div>
              <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>{latest.content.commoditizationRisk.why}</p>
            </div>
            <div className="rr-stack rr-stack--2">
              <Label>Perguntas que ainda não respondemos</Label>
              {latest.content.questions.map((q, i) => {
                const sug = questionSuggestions.find((s) => s.payload.text === q);
                return (
                  <div key={i} className="rr-challenge__q">
                    <span className="rr-arrow">→</span>
                    <div className="rr-stack rr-stack--2" style={{ flex: 1 }}>
                      <span>{q}</span>
                      {sug && canEdit && <Button size="sm" variant="ghost" onClick={() => accept.mutate(sug.id)} disabled={accept.isPending} style={{ alignSelf: 'flex-start' }}>investigar →</Button>}
                      {!sug && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>já na lista de perguntas</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="rr-stack rr-stack--2">
              <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{latest.model} · {formatDate(latest.createdAt)} · isso ajudou?</span>
              <div className="rr-feedback">
                {AI_FEEDBACK.map((f) => <button key={f} type="button" aria-pressed={latest.feedback === f} onClick={() => feedback.mutate({ id: latest.id, f })}>{AI_FEEDBACK_LABELS[f]}</button>)}
              </div>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
